import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import ShareModal from '../UI/ShareModal/ShareModal';
import PriceConfirmDialog from '../UI/PriceConfirmDialog/PriceConfirmDialog';
import { cardsAPI } from '../../api/modules/cards.module';
import { userModule } from '../../api/modules/user.module';
import { showToast } from '../../utils/toast';
import useDebounce from '../../hooks/useDebounce';
import { useAddToCart } from '../../hooks/useAddToCart';
import { formatPrettyDate } from '../../utils/common';
import { useFavourites } from '../../favourites/useFavourites';
import SellerStoreView from './SellerStore.view';
import { quickAddListing } from './quickAddListing';
import { CLEARED_FILTERS } from '../Marketplace/components/MarketplaceFilters/filterModel';

/**
 * Seller store, the container. Two queries, the URL parameters, the share
 * surface and the page state. Everything visual lives in
 * `SellerStore.view.jsx`, which is pure.
 *
 * WHAT WENT AWAY
 * --------------
 * - 98 lines of pagination, byte-identical to the copy that was in
 *   `Marketplace.jsx`. Both are now the shared `Pagination`.
 * - A local `renderStars` plus `convertPercentageToRating` and
 *   `getRatingValue`, three functions doing one job across three different
 *   scales (a percentage string, a 0 to 5 float, and a 0 to 100 int that got
 *   divided by 20). All of it is now ONE normalisation, `storeRating` below,
 *   feeding the shared `Rating`.
 * - A second hand-inlined 18px filter SVG, an inlined green shield "verified"
 *   SVG with two hardcoded hexes (`#4FED27` and `white`) that could not
 *   respond to the theme, and a hand-built search input.
 * - `SellerStore.css`, and the 480px `matchMedia` that was the page's own
 *   private breakpoint.
 * - `Layout` from `SellerDashboard/`, seller-console chrome around a buying
 *   page. It is `MarketShell` now.
 * - An unused `axios` import.
 *
 * A BUG FIXED IN PASSING. `fetchSellerCards` had no in-flight guard, so with a
 * debounced search plus filter changes a slow earlier response could overwrite
 * a newer one, exactly the race `Marketplace.jsx` already guarded against. The
 * same monotonic sequence guard is applied here.
 *
 * `available` ON THIS ROUTE IS `Sum(quantity)`, where the identical field on
 * `/marketplace` is `Count(listings)`. Flagged, not designed around: the view
 * labels the two differently and neither page recomputes the number.
 */

const PAGE_SIZE = 10;

const SORT_OPTIONS = [
  { value: 'l-h', label: 'Price: Low to High' },
  { value: 'h-l', label: 'Price: High to Low' },
  { value: 'a-z', label: 'Name: A to Z' },
  { value: 'z-a', label: 'Name: Z to A' },
];

/**
 * One rating, one scale. The API can express the same number three ways and
 * the page used to carry a converter for each:
 *
 *   storeInfo.stats.positive_rating        "92%"  a percentage STRING
 *   seller.seller_rating_stats.rating_out_of_5    already 0 to 5
 *   seller.rating                          0 to 100, the legacy field
 *
 * Everything below returns a 0 to 5 float or null. Null means "no rating yet",
 * which is not the same as zero and must not render as one star out of five.
 */
function storeRating(storeInfo, sellerInfo) {
  const positive = storeInfo?.stats?.positive_rating;
  if (positive) {
    const percentage = parseFloat(String(positive).replace('%', ''));
    if (Number.isFinite(percentage)) return (percentage / 100) * 5;
  }
  const outOfFive = sellerInfo?.seller_rating_stats?.rating_out_of_5;
  if (outOfFive != null) return Number(outOfFive);
  if (sellerInfo?.rating) return Number(sellerInfo.rating) / 20;
  return null;
}

const SellerStore = () => {
  const { sellerId } = useParams();
  const location = useLocation();
  /* `confirm` is the pending price-confirmation question, or `null` -- see
     `useAddToCart`'s "A BLOCKED ADD IS A QUESTION, NOT A DEAD END" docstring.
     The quick-add buttons on this grid had no `disabled` state of their own
     (see that hook's "THE AWAIT BREAKS THE CART'S ATOMICITY" note), so before
     this a blocked quick-add here was silent AND left the tile's re-entry
     claim held until reload -- nothing ever told the buyer, or gave the
     claim back.

     `adding` is that missing state: a map keyed by LISTING id, true while that
     listing's refresh is in flight, cleared in a `finally` on every exit. It is
     an ADDITIONAL gate, never a replacement for the hook's own synchronous
     per-listing claim -- that claim is held until the buyer answers a dialog,
     which is longer than `adding` lives, and shortening it would make a
     currently-unreachable defect (a second question for the same listing
     collapsing rather than superseding) reachable. */
  const { addToCart, quantityInCart, confirm, adding } = useAddToCart({
    /* PATCH THE TILE FROM THE RESPONSE. Defined below, threaded in here. */
    onListingRefreshed: (row) => patchTileFromRefresh(row),
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /* The heart. Before this the grid kept its own Set, which persisted nothing
     for anyone; `useFavourites` is localStorage for a guest and the API for a
     signed-in buyer, and this page does not need to know which. */
  const favourites = useFavourites();

  const [sellerInfo, setSellerInfo] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('l-h');
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCards, setTotalCards] = useState(0);

  const [filters, setFilters] = useState({
    category: 'All',
    rarity: [],
    condition: [],
    priceRange: [0, 1000],
    productType: searchParams.get('productType') || '',
    tcg_category_id: searchParams.get('tcg_category_id') || '',
    catalog_group_id: searchParams.get('catalog_group') || '',
    set: searchParams.get('set') || '',
    available_to_buy: true,
  });

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    const setParam = searchParams.get('set');
    const catalogGroupParam = searchParams.get('catalog_group');
    const productTypeParam = searchParams.get('productType');
    const tcgCategoryParam = searchParams.get('tcg_category_id');
    if (setParam || catalogGroupParam || productTypeParam || tcgCategoryParam) {
      setFilters((prev) => ({
        ...prev,
        set: setParam || prev.set,
        catalog_group_id: catalogGroupParam || prev.catalog_group_id,
        productType: productTypeParam || prev.productType,
        tcg_category_id: tcgCategoryParam || prev.tcg_category_id,
      }));
    }
  }, [searchParams]);

  // Monotonic id per fetch. Without it, a slow earlier request (an old search
  // term, a filter the user has already changed) can resolve after a newer one
  // and overwrite the current results. Same guard Marketplace.jsx carries.
  const fetchSeqRef = useRef(0);

  const fetchSellerCards = useCallback(async () => {
    const fetchSeq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);

    try {
      const apiParams = {
        seller_id: sellerId,
        sort: sortBy,
        offset: PAGE_SIZE,
        index: (page - 1) * PAGE_SIZE,
      };

      if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
        apiParams.search = debouncedSearchQuery.trim();
      }

      if (filters.productType === 'card') {
        apiParams.card = true;
      } else if (filters.productType === 'sealed') {
        apiParams.sealed = true;
      }

      if (filters.category && filters.category !== 'All' && filters.category !== '') {
        apiParams.category = filters.category;
      }

      // Pass rarity and condition as arrays directly (same as Marketplace)
      if (Array.isArray(filters.rarity) && filters.rarity.length > 0) {
        apiParams.rarity = filters.rarity;
      }

      if (Array.isArray(filters.condition) && filters.condition.length > 0) {
        apiParams.condition = filters.condition;
      }

      if (
        filters.priceRange &&
        Array.isArray(filters.priceRange) &&
        filters.priceRange.length === 2
      ) {
        const minPrice = filters.priceRange[0];
        const maxPrice = filters.priceRange[1];
        if (minPrice > 0 || maxPrice < 1000) {
          apiParams.min_price = minPrice;
          apiParams.max_price = maxPrice;
        }
      }

      if (filters.tcg_category_id && filters.tcg_category_id !== '') {
        apiParams.tcg_category_id = filters.tcg_category_id;
      }

      if (filters.catalog_group_id && filters.catalog_group_id !== '') {
        apiParams.catalog_group_id = filters.catalog_group_id;
      }

      if (filters.set && filters.set !== '') {
        apiParams.set = filters.set;
      }

      if (typeof filters.available_to_buy !== 'undefined') {
        apiParams.available_to_buy = filters.available_to_buy;
      }

      const data = await cardsAPI.getSellerCards(apiParams);
      if (fetchSeq !== fetchSeqRef.current) return;

      if (data?.data) {
        setCards(data.data);
        setTotalCards(data.total || 0);
        setTotalPages(Math.ceil((data.total || 0) / PAGE_SIZE));

        // The cards endpoint carries the seller on every row, so the first one
        // is the fallback identity when the profile query has not landed.
        if (data.data.length > 0) {
          setSellerInfo(data.data[0].seller || null);
        }
      }
    } catch (err) {
      if (fetchSeq !== fetchSeqRef.current) return;
      setError('Failed to load seller cards. Please try again.');
      showToast.error('Failed to load seller cards');
      setCards([]);
    } finally {
      if (fetchSeq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [sellerId, sortBy, page, debouncedSearchQuery, filters, fetchSeqRef]);

  /* Same origin hand-off as `/search` and `/collection`: a buyer who opened a
     card from inside a store was returned to the marketplace, not the store
     they were shopping. The store's own name is the label, because "back to
     the store" is vaguer than the shop sign they just walked out of. */
  const handleCardClick = (cardId) => {
    navigate(`/marketplace/card/${cardId}`, {
      state: {
        from: `/seller/${sellerId}`,
        /* `storeName` falls back to "Unknown seller", and "Back to Unknown
           seller" is worse than saying nothing specific. */
        fromLabel: storeName && storeName !== 'Unknown seller' ? storeName : 'the store',
      },
    });
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setPage(1);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);

    // Update URL params like Marketplace
    const params = new URLSearchParams();
    if (newFilters.set) {
      params.set('set', newFilters.set);
    }
    if (newFilters.catalog_group_id) {
      params.set('catalog_group', newFilters.catalog_group_id);
    }
    if (newFilters.tcg_category_id) {
      params.set('tcg_category_id', newFilters.tcg_category_id);
    }
    if (newFilters.productType) {
      params.set('productType', newFilters.productType);
    }
    setSearchParams(params, { replace: true });
  };

  /* Clear everything narrowing this store's shelf, and STAY IN THE STORE.
     Same fault the marketplace had: `isFiltered` counts `searchQuery`, so a
     query that matched nothing raised the filtered empty state with no facets
     applied and its action cleared filters that were already clear. The store's
     search is local state rather than context, but the shape of the fix is
     identical.

     No navigation. Clearing a store's filters means "show me everything this
     seller has", not "leave". Sending it to the home page would eject a buyer
     from the store they deliberately opened, with the back button as the only
     route to its full catalogue. */
  const handleClearAll = () => {
    setSearchQuery('');
    handleFilterChange(CLEARED_FILTERS);
  };

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  /* REVIEWS, FETCHED ON OPEN.
     The store page's job on load is the grid; reviews are a thing a buyer asks
     for by tapping the stars, and most never will. React Query's `enabled`
     keeps the request off the page load entirely and then caches it, so opening
     the panel a second time costs nothing. */
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const reviewsQuery = useQuery({
    queryKey: ['sellerReviews', sellerId],
    enabled: reviewsOpen && Boolean(sellerId),
    queryFn: async () => {
      const response = await userModule.getSellerReviews(sellerId);
      if (!response.success) throw new Error(response.message || 'Failed to load reviews');
      return response;
    },
  });

  // Use React Query to fetch seller store info
  const sellerInfoQuery = useQuery({
    queryKey: ['sellerInfo', sellerId],
    queryFn: async () => {
      const response = await userModule.getSellerInfoById(sellerId);
      if (!response.success) throw new Error(response.message || 'Failed to load seller info');
      return response.data?.details || response.data?.data || response.data;
    },
    enabled: !!sellerId,
  });

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    if (sellerId) {
      fetchSellerCards();
    }
  }, [sellerId, sortBy, page, debouncedSearchQuery, filters]);

  useEffect(() => {
    if (debouncedSearchQuery !== undefined) {
      setPage(1);
    }
  }, [debouncedSearchQuery]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  const { data: storeInfo, isLoading: storeLoading, isError: storeError } = sellerInfoQuery;

  const storeName =
    storeInfo?.store_name || sellerInfo?.full_name || sellerInfo?.name || 'Unknown seller';

  const joinedAt = storeInfo?.created_at || sellerInfo?.created_at;

  /* The rule itself lives in `quickAddListing` so it can be tested without
     mounting this page's shell. */
  const quickAddFor = useCallback((card) => quickAddListing(card, sellerId), [sellerId]);

  /* THE TILE, PATCHED FROM THE REFRESH RESPONSE.
     ---------------------------------------------
     `refresh-card-prices` answers with the listing rows it just re-derived
     (`useAddToCart` hands them here through `onListingRefreshed`, confirmed or
     not), and NOTHING refetches `seller-card-lookup/` afterwards -- it is
     called once per filter/sort/page change and an add is none of those. So
     without this the tile a buyer just watched get confirmed would keep its
     unconfirmed marker and its pre-refresh figure for the life of the page,
     while the cart it went into holds the new one. Same move
     `Marketplace/ListDetail/CardDetail.jsx` makes with `listed_cards`.

     MATCHED ON `listing_id`, WHICH ONLY A ONE-LISTING TILE HAS. That is also
     what makes the two quantity fields commensurable: `available` on this
     endpoint is `Sum(quantity)` across the seller's listings for the card, and
     a tile with exactly one listing is the case where that sum IS this
     listing's stock. An ambiguous tile carries `listing_id: null`, never
     matches, and is left alone.

     A SOLD-OUT LISTING NEVER REACHES HERE, so this patch cannot report one.
     `refresh-card-prices` filters `listed_cards` on `quantity__gt=0`
     (`apps/api/apps/cards/views.py`), so a listing that sold out is simply
     ABSENT from the response: `fresh` is `undefined` in `useAddToCart`,
     `onListingRefreshed` is never called, and this function never runs for it.
     The reachable outcome is the opposite of a tile emptying itself -- the tile
     KEEPS "2 in stock", keeps its figure and keeps its marker, and only the
     unconfirmable dialog says anything at all. `available` is patched here for
     the case that DOES arrive: a live listing whose stock moved between the
     page load and the tap.

     EVERY FIELD IS GUARDED SEPARATELY because the row is the server's, not
     ours: `price` arrives as the DRF `DecimalField` STRING "810.00" where the
     tile holds the JSON number 810.15, and a row that somehow omitted a field
     must leave the tile's own value standing rather than blank it. */
  const patchTileFromRefresh = useCallback((row) => {
    if (!row || row.id == null) return;
    const listingId = String(row.id);
    const price = Number(row.price);
    const quantity = Number(row.quantity);

    /* WHAT THE TILE IS ALLOWED TO CALL CONFIRMED -- the SAME pair
       `useAddToCart` refuses on, not `price_stale` alone (fix round 1).
       ------------------------------------------------------------------
       The two fields answer different questions and are documented as
       diverging: `is_listing_price_unsellable` (`apps/api/apps/cards/pricing.py`)
       reads the CARD-WIDE `Card.price_retrieved_at`, which `get_fresh` stamps
       whenever any price row comes back, while `get_price_basis_confirmed`
       (`apps/api/apps/cards/serializers.py`) is per (finish, condition).
       A Damaged tracked listing on a card whose refresh returned Near Mint rows
       only therefore comes back `price_stale: false, price_basis_confirmed:
       false` -- and writing `price_stale` STRAIGHT ACROSS took the marker off
       the tile at the very moment the dialog said the price could not be
       confirmed. Worse, it left the tile unblocked for the life of the page:
       the next tap took `useAddToCart`'s `!isPriceUnavailable(item)` branch and
       committed to the cart SILENTLY, no refresh and no question, at a figure
       `reprice_listing` never re-derived. Exactly the fail-open
       `price_basis_confirmed` was added to close, reintroduced one layer up.

       AN ABSENT `price_basis_confirmed` COUNTS AS UNCONFIRMED, so a server that
       stopped sending it leaves tiles marked rather than silently clearing them
       -- the same direction `useAddToCart` fails in.

       THIS LEAVES THE UI STRICTER THAN THE SERVER, DELIBERATELY.
       `create_order_and_payment` refuses on `is_listing_price_unsellable`
       alone, so a basis-unconfirmed listing whose card was stamped fresh would
       be SELLABLE at checkout while this tile still says "Price unconfirmed".
       That is the posture this codebase already takes (see the same pair in
       `useAddToCart`'s "WHAT COUNTS AS CONFIRMED") and it is the safe
       direction: over-marking costs a buyer a dialog, under-marking sells a
       price nobody re-derived. */
    const confirmed = row.price_stale === false && row.price_basis_confirmed === true;

    setCards((current) =>
      current.map((card) => {
        if (!card.listing_id || String(card.listing_id) !== listingId) return card;
        return {
          ...card,
          /* `!confirmed`, never `row.price_stale`. The guard is on the server
             having ANSWERED at all: with no boolean there the tile keeps the
             value it was rendered with rather than being marked on a silence. */
          ...(typeof row.price_stale === 'boolean' ? { price_stale: !confirmed } : null),
          /* `price_unconfirmed` GOES STRAIGHT ACROSS -- and that is correct
             precisely BECAUSE the line above does not.
             --------------------------------------------------------------
             The two fields answer different questions and the tile's marker
             (and `useAddToCart`'s gate) read the UNION of them, so each has to
             be written with its own truth:

               - `price_unconfirmed` is "was this card ATTEMPTED today?". The
                 refresh just attempted it, whatever came back, so a row that
                 answers this field answers it honestly and the tile takes the
                 answer as given. Omitting it is not the safe direction it
                 looks like: the tile would keep its marker for the LIFE OF
                 THE PAGE after a perfectly successful confirm.
               - `price_stale` is "may this be sold?", and is written as
                 `!confirmed` for the reason above -- half an answer must not
                 clear the marker.

             The PAIRING is what makes both safe. A refresh that stamps
             `price_checked_at` but leaves this listing's basis unconfirmed
             clears the first and SETS the second, so the union still catches
             the next tap and the marker never comes off a tile whose price
             nothing re-derived. Writing `price_unconfirmed` on its own,
             without the union gate, would re-land the defect the block
             comment above records. */
          ...(typeof row.price_unconfirmed === 'boolean'
            ? { price_unconfirmed: row.price_unconfirmed }
            : null),
          ...(row.pricing_mode ? { pricing_mode: row.pricing_mode } : null),
          /* One figure, two names. `seller-card-lookup` writes the seller's
             cheapest listing into BOTH `lowest_price` and `market_price` (see
             that view), and the grid reads `market_price`; leaving one behind
             would show the old price under a marker that had just cleared.
             Written whether or not the price was CONFIRMED, because it is the
             figure the server holds for this listing either way -- and an
             unconfirmed one still carries its marker to say so. */
          ...(Number.isFinite(price) ? { lowest_price: price, market_price: price } : null),
          ...(Number.isFinite(quantity) ? { available: quantity } : null),
        };
      })
    );
  }, []);

  /* The ids already in the cart, so a tile that has been added says so rather
     than inviting a second tap. */
  const quickAddedIds = new Set(
    cards.map((card) => card.listing_id).filter((id) => id && quantityInCart(id) > 0)
  );

  /* The ids whose refresh is in flight. Same shape as `quickAddedIds` above and
     rebuilt the same way, per render, off a map that only changes when an add
     starts or ends. */
  const quickAddPendingIds = new Set(Object.keys(adding).filter((id) => adding[id]));

  return (
    <>
      <SellerStoreView
        backTo={location.state?.from}
        backToLabel={location.state?.fromLabel}
        storeName={storeName}
        logo={storeInfo?.store_logo || sellerInfo?.avatar || undefined}
        verified={Boolean(storeInfo?.is_verified ?? storeInfo?.verified)}
        rating={storeRating(storeInfo, sellerInfo)}
        salesCount={storeInfo?.stats?.items_sold ?? sellerInfo?.sales ?? undefined}
        joined={joinedAt ? formatPrettyDate(joinedAt) : undefined}
        location={storeInfo?.location || sellerInfo?.location || undefined}
        storeLoading={storeLoading}
        /* An errored profile query with no cards to fall back on is the only
         honest "no such store". A profile that failed while cards arrived is
         still a real store, so it keeps rendering. */
        storeNotFound={Boolean(storeError) && !loading && cards.length === 0}
        onShare={() => setIsShareModalOpen(true)}
        cards={cards}
        loading={loading}
        error={error}
        onRetry={fetchSellerCards}
        totalCards={totalCards}
        page={page}
        totalPages={totalPages}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        sortOptions={SORT_OPTIONS}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onCardClick={handleCardClick}
        /* `total_ratings` is what the percentage rests on, and what the band's
         control counts. Without it "100% positive" could be one rating. */
        reviewCount={storeInfo?.stats?.total_ratings ?? 0}
        reviewsOpen={reviewsOpen}
        onOpenReviews={() => setReviewsOpen(true)}
        onCloseReviews={() => setReviewsOpen(false)}
        reviews={reviewsQuery.data?.data || []}
        reviewsLoading={reviewsQuery.isPending && reviewsOpen}
        reviewsError={reviewsQuery.isError ? reviewsQuery.error?.message : undefined}
        onRetryReviews={reviewsQuery.refetch}
        quickAddFor={quickAddFor}
        onQuickAdd={(listing) => addToCart(listing, 1)}
        quickAddedIds={quickAddedIds}
        quickAddPendingIds={quickAddPendingIds}
        savedIds={favourites.ids}
        onToggleSaved={(cardId) => favourites.toggle(cardId)}
        filtersOpen={filtersOpen}
        onOpenFilters={() => setFiltersOpen(true)}
        onCloseFilters={() => setFiltersOpen(false)}
        sortOpen={sortOpen}
        onOpenSort={() => setSortOpen(true)}
        onCloseSort={() => setSortOpen(false)}
        shareSurface={
          <ShareModal
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            title={`Share ${storeName}`}
            description={`Check out ${storeName}'s card collection on Sleevr!`}
            url={window.location.href}
          />
        }
      />
      <PriceConfirmDialog confirm={confirm} />
    </>
  );
};

export default SellerStore;
